import React, { useCallback, useState } from "react"
import { useSetRecoilState } from "recoil"
import { useMobergMenu } from "../../../../../../Hooks/useMobergMenu"
import { useAnnotationService } from "../../../../../../Hooks/useAnnotationService"
import { useModalProvider } from "../../../../../../Providers/ModalProvider"
import { EditAnnotation } from "../../../../../../Managers/VisualizationManager/ToolBar/Components/EditAnnotation"
import ConfirmationModal from "../../../../../../Components/ConfirmationModal/ConfirmationModal"
import { MobergIconSize, MobergTheme } from "../../../../../../Moberg"
import { Annotation } from "../../../../../../Managers/VisualizationManager/Variables/Annotations"
import { annotationZOrderAtom, selectedAnnotationAtom } from "../../Atoms/Annotations"
import { MdDeleteOutline, MdOutlineEdit, MdFlipToBack, MdVisibility } from "react-icons/md"
import { MobergContextMenuItem } from "../../../../../../Components/MobergMenu/MobergContextMenuItem"
import { useDataReviewPermissions } from "../../../../../../Hooks/useDataReviewPermissions"
import { permissionTooltip } from "../../../../../../Utils/permissionUtils"

export const useAnnotationContextMenu = (windowId: string) => {
	const { deleteAnnotations } = useAnnotationService()
	const { createModal } = useModalProvider()
	const setAnnotationZOrder = useSetRecoilState(annotationZOrderAtom(windowId))
	const setSelectedAnnotation = useSetRecoilState(selectedAnnotationAtom)
	const [menuAnnotation, setMenuAnnotation] = useState<Annotation | null>(null)
	const menu = useMobergMenu({ placement: "bottom-start", isContextMenu: true })
	const { canManageDataReview } = useDataReviewPermissions()
	const canModifyMenuAnnotation = canManageDataReview && !menuAnnotation?.read_only

	const moveAnnotationToBack = useCallback(
		(annotationId: number) => {
			setAnnotationZOrder(currentOrder => {
				const newOrder = currentOrder.filter(id => id !== annotationId)
				return [annotationId, ...newOrder]
			})
		},
		[setAnnotationZOrder]
	)

	const openAnnotationContextMenu = useCallback(
		(event: React.MouseEvent, annotation: Annotation) => {
			event.preventDefault()
			setMenuAnnotation(annotation)
			setSelectedAnnotation(annotation)
			menu.open(event)
		},
		[menu, setSelectedAnnotation]
	)

	const handleOpenAnnotation = useCallback(() => {
		if (!menuAnnotation) return

		menu.close()
		createModal(<EditAnnotation />)
	}, [createModal, menu, menuAnnotation])

	const handleMoveToBack = useCallback(() => {
		if (menuAnnotation) {
			moveAnnotationToBack(menuAnnotation.id)
		}
		menu.close()
	}, [menu, menuAnnotation, moveAnnotationToBack])

	const handleDelete = useCallback(() => {
		if (!menuAnnotation || !canModifyMenuAnnotation) return

		menu.close()
		createModal(
			<ConfirmationModal
				title="Delete annotation?"
				description="Are you sure you want to permanently delete this annotation? This action cannot be undone."
				confirmButton={{
					text: "Yes, delete annotation",
					theme: MobergTheme.RED,
					onClick: () => deleteAnnotations([menuAnnotation.id])
				}}
			/>
		)
	}, [canModifyMenuAnnotation, createModal, deleteAnnotations, menu, menuAnnotation])

	const AnnotationContextMenu = (
		<menu.MobergMenu>
			<MobergContextMenuItem
				onClick={handleOpenAnnotation}
				icon={canModifyMenuAnnotation ? <MdOutlineEdit size={MobergIconSize.SMALL} /> : <MdVisibility size={MobergIconSize.SMALL} />}
				disabled={!menuAnnotation}
			>
				{canModifyMenuAnnotation ? "Edit annotation" : "View annotation"}
			</MobergContextMenuItem>
			<MobergContextMenuItem onClick={handleMoveToBack} icon={<MdFlipToBack size={MobergIconSize.SMALL} />}>
				Move to back
			</MobergContextMenuItem>
			{canModifyMenuAnnotation && (
				<MobergContextMenuItem onClick={handleDelete} icon={<MdDeleteOutline size={MobergIconSize.SMALL} />} theme={MobergTheme.RED} tooltip={permissionTooltip(canManageDataReview)}>
					Delete annotation
				</MobergContextMenuItem>
			)}
		</menu.MobergMenu>
	)

	return { AnnotationContextMenu, openAnnotationContextMenu }
}
